/**
 * Elle ödev atama.
 *
 * Program önerisi bir başlangıç noktası; burası uygulayıcının tam
 * kontrolü. Bir çocuğa yalnızca kutu sırası, bir başkasına dört
 * egzersiz birden gönderebilmesi gerekiyor ve bunun planlayıcıdan
 * geçmesi için bir sebep yok.
 *
 * Her satırın düzeyi ve kanalı ayrı: aynı çocuğa bir egzersizi kolay,
 * bir başkasını zor vermek isteyebilir. Kâğıda basılamayan şablon
 * kâğıt seçilemiyor — sessizce dijitale çevirmek yerine seçeneği hiç
 * sunmamak gerekiyor, yoksa uygulayıcı ne atadığını bilmez.
 */

import { useCallback, useEffect, useState } from 'react'
import { ALAN_ADI, ApiHatasi, api, type KatalogSatiri } from '../api'

interface Secim {
  duzey: number
  kanal: 'digital' | 'print'
  adet: number
}

function sureYaz(sn: number): string {
  if (sn < 60) return `${sn} sn`
  return `${Math.round(sn / 60)} dk`
}

export function OdevAta({
  danisanId,
  onBitti,
  onIptal,
}: {
  danisanId: string
  onBitti: () => void
  onIptal: () => void
}) {
  const [duzey, setDuzey] = useState(5)
  const [katalog, setKatalog] = useState<KatalogSatiri[] | null>(null)
  const [secim, setSecim] = useState<Record<string, Secim>>({})
  const [sonTarih, setSonTarih] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const yukle = useCallback(async () => {
    try {
      const r = await api.egzersizKatalogu(duzey)
      setKatalog(r.katalog)
    } catch {
      setHata('Katalog alınamadı.')
      setKatalog([])
    }
  }, [duzey])

  useEffect(() => {
    void yukle()
  }, [yukle])

  const secili = Object.entries(secim)
  const toplamAdet = secili.reduce((a, [, s]) => a + s.adet, 0)
  const toplamSn = secili.reduce((a, [kod, s]) => {
    const k = katalog?.find((x) => x.kod === kod)
    return a + (k?.saniye ?? 0) * s.adet
  }, 0)

  const degistir = (k: KatalogSatiri, yeni: Partial<Secim> | null) => {
    setSecim((eski) => {
      const kopya = { ...eski }
      if (yeni === null) {
        delete kopya[k.kod]
        return kopya
      }
      const mevcut = kopya[k.kod] ?? {
        duzey,
        // Kâğıda basılamayan şablonda kanal seçeneği yok
        kanal: k.kanallar.includes('digital') ? 'digital' : 'print',
        adet: 1,
      }
      kopya[k.kod] = { ...mevcut, ...yeni }
      return kopya
    })
  }

  const ata = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      await api.odevAta({
        danisanId,
        ...(sonTarih ? { sonTarih } : {}),
        egzersizler: secili.map(([kod, s]) => ({ kod, ...s })),
      })
      onBitti()
    } catch (e) {
      const k = e instanceof ApiHatasi ? e.kod : ''
      setHata(
        k === 'onam_yok'
          ? 'Program onamı alınmadan ödev atanamıyor. Dosyadan onamı kaydedin.'
          : k === 'kanal_uyumsuz'
            ? 'Seçtiğiniz şablonlardan biri o kanalda çalışmıyor.'
            : 'Ödev atanamadı.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onIptal}>← Dosya</button>
      </div>

      <h1>Ödev ata</h1>
      <p className="alt">
        Hangi egzersizi, hangi düzeyde, kaç tane vereceğinizi siz seçiyorsunuz. Program
        önerisinden geçmesi gerekmiyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      <div className="kart">
        <div className="satir">
          <label>
            Varsayılan düzey
            <input
              type="number"
              min={1}
              max={10}
              value={duzey}
              onChange={(e) => setDuzey(Math.max(1, Math.min(10, Number(e.target.value))))}
            />
          </label>
          <label>
            Son tarih (boş bırakılabilir)
            <input type="date" value={sonTarih} onChange={(e) => setSonTarih(e.target.value)} />
          </label>
        </div>
        <p className="alt" style={{ margin: 0 }}>
          Düzey değişince katalogdaki süreler de değişir; her satırın düzeyini ayrıca
          ayarlayabilirsiniz.
        </p>
      </div>

      {katalog === null ? (
        <p className="alt">Katalog yükleniyor…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th />
              <th>Egzersiz</th>
              <th>Alan</th>
              <th>Süre</th>
              <th>Düzey</th>
              <th>Kanal</th>
              <th>Adet</th>
            </tr>
          </thead>
          <tbody>
            {katalog.map((k) => {
              const s = secim[k.kod]
              return (
                <tr key={k.kod} style={{ background: s ? 'var(--soft-accent)' : undefined }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(s)}
                      onChange={(e) => degistir(k, e.target.checked ? {} : null)}
                      style={{ width: 18 }}
                    />
                  </td>
                  <td>
                    <strong>{k.ad}</strong>
                    <div className="alt" style={{ margin: 0, fontSize: 12 }}>
                      {k.maddeSayisi ? `${k.maddeSayisi} madde` : ''}
                    </div>
                  </td>
                  <td className="alt" style={{ margin: 0, fontSize: 13 }}>
                    {k.alanlar.map((a) => ALAN_ADI[a] ?? a).join(', ')}
                  </td>
                  <td>{k.saniye ? sureYaz(k.saniye) : '—'}</td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={s?.duzey ?? duzey}
                      disabled={!s}
                      onChange={(e) =>
                        degistir(k, { duzey: Math.max(1, Math.min(10, Number(e.target.value))) })
                      }
                      style={{ width: 70 }}
                    />
                  </td>
                  <td>
                    <select
                      value={s?.kanal ?? (k.kanallar.includes('digital') ? 'digital' : 'print')}
                      disabled={!s || k.kanallar.length < 2}
                      onChange={(e) => degistir(k, { kanal: e.target.value as never })}
                      style={{ width: 110 }}
                    >
                      {k.kanallar.includes('digital') && <option value="digital">Tablet</option>}
                      {k.kanallar.includes('print') && <option value="print">Kâğıt</option>}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      max={5}
                      value={s?.adet ?? 1}
                      disabled={!s}
                      onChange={(e) =>
                        degistir(k, { adet: Math.max(1, Math.min(5, Number(e.target.value))) })
                      }
                      style={{ width: 70 }}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div className="kart" style={{ marginTop: 14 }}>
        {secili.length === 0 ? (
          <p style={{ margin: 0 }}>Henüz egzersiz seçilmedi.</p>
        ) : (
          <p style={{ margin: 0 }}>
            <strong>{secili.length}</strong> şablon, <strong>{toplamAdet}</strong> sayfa,
            yaklaşık <strong>{Math.max(1, Math.round(toplamSn / 60))} dakika</strong>.
            {toplamAdet > 8 && (
              <span style={{ color: 'var(--warn)' }}>
                {' '}
                Bu kadar sayfa tek oturumda çok olabilir.
              </span>
            )}
          </p>
        )}
        <div className="dugmeler">
          <button
            className="birincil"
            disabled={bekliyor || secili.length === 0}
            onClick={() => void ata()}
          >
            {bekliyor ? 'Atanıyor…' : 'Ödevleri ata'}
          </button>
          <button onClick={onIptal} disabled={bekliyor}>
            Vazgeç
          </button>
        </div>
      </div>
    </>
  )
}
